import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import api from '../../services/api';
import { FiEdit2, FiTrash2, FiPlus, FiFilter } from 'react-icons/fi';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
`;

const Title = styled.h2`
  color: #fff;
  font-size: 24px;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const AddButton = styled.button`
  padding: 10px 20px;
  background: #4caf50;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #45a049;
    transform: translateY(-2px);
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  padding: 15px;
  background: #0f0f0f;
  border-radius: 8px;
  border: 1px solid #333;
`;

const Select = styled.select`
  padding: 8px 12px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const Table = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 12px;
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr 1fr 0.8fr 0.8fr;
  gap: 15px;
  padding: 16px;
  background: #1a1a1a;
  border-bottom: 1px solid #333;
  font-weight: 600;
  color: #999;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  min-width: 900px;
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1fr 1fr 0.8fr 0.8fr;
  gap: 15px;
  padding: 16px;
  border-bottom: 1px solid #333;
  align-items: center;
  transition: all 0.3s ease;
  min-width: 900px;

  &:hover {
    background: #1a1a1a;
  }
`;

const ItemName = styled.p`
  color: #fff;
  font-weight: 600;
  margin: 0;
  font-size: 14px;
`;

const Category = styled.p`
  color: #999;
  margin: 0;
  font-size: 13px;
`;

const Price = styled.p`
  color: #fff;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
`;

const Badge = styled.span`
  padding: 4px 8px;
  background: ${props => props.$active ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'};
  color: ${props => props.$active ? '#4caf50' : '#f44336'};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  width: fit-content;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  padding: 6px 10px;
  background: none;
  border: 1px solid #333;
  border-radius: 4px;
  color: #ff6b35;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: #ff6b35;
    color: #fff;
    border-color: #ff6b35;
  }
`;

const DeleteButton = styled(ActionButton)`
  color: #f44336;

  &:hover {
    background: #f44336;
  }
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #999;
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: #0f0f0f;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 30px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  color: #fff;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  color: #999;
  font-size: 12px;
  text-transform: uppercase;
  display: block;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const Checkbox = styled.input`
  margin-right: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 10px;
  background: #4caf50;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #45a049;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 10px;
  background: #666;
  border: none;
  border-radius: 6px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #555;
  }
`;

const AdminItems = () => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    service_type: '',
    gender: '',
    is_active: ''
  });
  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    item_name: '',
    price: '',
    unit: 'per item',
    service_type: '',
    is_active: true,
    sort_order: 0
  });

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [items, filters]);

  const fetchItems = async () => {
    try {
      const response = await api.adminGetItems();
      if (response.success && response.data) {
        setItems(response.data);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching items:', err);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...items];

    if (filters.category) {
      filtered = filtered.filter(item => item.category === filters.category);
    }
    if (filters.service_type) {
      filtered = filtered.filter(item => item.service_type === filters.service_type);
    }
    if (filters.gender) {
      filtered = filtered.filter(item => item.gender === filters.gender);
    }
    if (filters.is_active !== '') {
      filtered = filtered.filter(item => item.is_active == filters.is_active);
    }

    setFilteredItems(filtered);
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        category: item.category,
        subcategory: item.subcategory || '',
        item_name: item.item_name,
        price: item.price,
        unit: item.unit || 'per item',
        service_type: item.service_type,
        is_active: Boolean(item.is_active),
        sort_order: item.sort_order || 0
      });
    } else {
      setEditingItem(null);
      setFormData({
        category: '',
        subcategory: '',
        item_name: '',
        price: '',
        unit: 'per item',
        service_type: '',
        is_active: true,
        sort_order: 0
      });
    }
    setShowModal(true);
  };

  const handleSaveItem = async () => {
    if (!formData.category || !formData.item_name || !formData.price || !formData.service_type) {
      alert('Please fill in required fields: Service Type, Category, Item Name, Price');
      return;
    }

    try {
      if (editingItem) {
        const response = await api.adminUpdateItem(editingItem.id, formData);
        if (response.success) {
          setItems(items.map(i => i.id === editingItem.id ? { ...i, ...formData } : i));
        }
      } else {
        const response = await api.adminCreateItem(formData);
        if (response.success && response.data) {
          fetchItems();
        }
      }
      setShowModal(false);
    } catch (err) {
      console.error('Error saving item:', err);
      alert('Failed to save item: ' + (err.message || 'Unknown error'));
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        const response = await api.adminDeleteItem(itemId);
        if (response.success) {
          setItems(items.filter(i => i.id !== itemId));
        } else {
          alert(response.message || 'Failed to delete item');
        }
      } catch (err) {
        console.error('Error deleting item:', err);
        alert('Failed to delete item: ' + (err.message || 'Unknown error'));
      }
    }
  };

  const uniqueCategories = [...new Set(items.map(i => i.category))];

  if (loading) {
    return <Container>Loading items...</Container>;
  }

  return (
    <Container>
      <Header>
        <Title>Items Catalog Management</Title>
        <HeaderActions>
          <AddButton onClick={() => handleOpenModal()}>
            <FiPlus /> Add Item
          </AddButton>
        </HeaderActions>
      </Header>

      <FilterContainer>
        <Select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
          <option value="">All Categories</option>
          {uniqueCategories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </Select>

        <Select value={filters.service_type} onChange={(e) => setFilters({ ...filters, service_type: e.target.value })}>
          <option value="">All Service Types</option>
          <option value="steam_iron">Steam Iron</option>
          <option value="dry_clean">Dry Clean</option>
          <option value="laundry">Laundry</option>
          <option value="household">Household</option>
          <option value="footwear">Footwear</option>
        </Select>

        <Select value={filters.gender} onChange={(e) => setFilters({ ...filters, gender: e.target.value })}>
          <option value="">All Genders</option>
          <option value="mens">Men's</option>
          <option value="womens">Women's</option>
          <option value="unisex">Unisex</option>
          <option value="household">Household</option>
        </Select>

        <Select value={filters.is_active} onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}>
          <option value="">All Status</option>
          <option value="1">Active</option>
          <option value="0">Inactive</option>
        </Select>
      </FilterContainer>

      {filteredItems.length > 0 ? (
        <Table>
          <TableHeader>
            <div>Item Name</div>
            <div>Category</div>
            <div>Price</div>
            <div>Service Type</div>
            <div>Status</div>
            <div>Actions</div>
          </TableHeader>
          {filteredItems.map((item, index) => (
            <TableRow
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
            >
              <ItemName>{item.item_name}</ItemName>
              <Category>{item.category}</Category>
              <Price>₹{parseFloat(item.price).toFixed(2)}</Price>
              <Category>{item.service_type}</Category>
              <Badge $active={item.is_active}>{item.is_active ? 'Active' : 'Inactive'}</Badge>
              <Actions>
                <ActionButton onClick={() => handleOpenModal(item)}>
                  <FiEdit2 />
                </ActionButton>
                <DeleteButton onClick={() => handleDeleteItem(item.id)}>
                  <FiTrash2 />
                </DeleteButton>
              </Actions>
            </TableRow>
          ))}
        </Table>
      ) : (
        <EmptyState>No items found. Add your first item!</EmptyState>
      )}

      {showModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowModal(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</ModalTitle>
            
            <FormGroup>
              <Label>Category *</Label>
              <Input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., Steam Iron - Mens"
              />
            </FormGroup>

            <FormGroup>
              <Label>Subcategory</Label>
              <Input
                type="text"
                value={formData.subcategory}
                onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                placeholder="e.g., Daily Wear, Formal"
              />
            </FormGroup>

            <FormGroup>
              <Label>Item Name *</Label>
              <Input
                type="text"
                value={formData.item_name}
                onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                placeholder="e.g., Shirt, Saree"
              />
            </FormGroup>

            <FormGroup>
              <Label>Price *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>Service Type *</Label>
              <Select
                value={formData.service_type}
                onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
              >
                <option value="steam_iron">Steam Iron</option>
                <option value="dry_clean">Dry Clean</option>
                <option value="laundry">Laundry</option>
                <option value="household">Household</option>
                <option value="footwear">Footwear</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Unit</Label>
              <Input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                placeholder="per item, per kg, per sq ft"
              />
            </FormGroup>

            <FormGroup>
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <Checkbox
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                Active
              </Label>
            </FormGroup>

            <ButtonGroup>
              <SaveButton onClick={handleSaveItem}>Save</SaveButton>
              <CancelButton onClick={() => setShowModal(false)}>Cancel</CancelButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default AdminItems;
